// src/stores/global.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import app from 'configs/appResolver';

import { logErrorSilent } from 'utils/errorLogger';
// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const localStorageKey = 'storage-registry';

// *******************************************************************************
// STATE
// *******************************************************************************
export const initialState = {};

// *******************************************************************************
// METHODS
// *******************************************************************************
function buildStorageKey() {
  return `${app.ID}:${localStorageKey}`;
}

export function getStorageRegistryItem(key) {
  const field = buildStorageKey();
  let lsData;
  try {
    lsData = JSON.parse(localStorage.getItem(field));
  } catch (e) {
    logErrorSilent(e, {
      context: 'globalStore.getStorageGlobalItem (JSON.parse)',
      extraData: { field, key },
    });
  }
  return lsData?.[key];
}

export function setStorageRegistryItem(key, value) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (value) {
    lsData[key] = value;
  } else {
    delete lsData[key];
  }
  localStorage.setItem(field, JSON.stringify(lsData));
}

export function removeStorageRegistryItem(key) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (lsData.hasOwnProperty(key)) {
    delete lsData[key];
    localStorage.setItem(field, JSON.stringify(lsData));
  }
}

// *******************************************************************************
// STORE
// *******************************************************************************
const store = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // Set state - supports context-based nesting
      // Usage:
      //   setState({ key: value })                    - merge into root (legacy)
      //   setState('context', { key: value })         - nest inside 'context' key
      //   setState('context')                         - remove 'context' key
      //   setState(null)                              - clear all state
      setState: (contextOrPayload, payload) => {
        // Case 1: setState(null) - clear all state
        if (contextOrPayload === null) {
          set({});
          return;
        }

        // Case 2: setState('context', payload) - context-based nesting
        if (typeof contextOrPayload === 'string') {
          const context = contextOrPayload;

          // Case 2a: setState('context') or setState('context', null/undefined) - remove context
          if (payload === undefined || payload === null) {
            set(state => {
              const newState = { ...state };
              delete newState[context];
              return newState;
            });
          }
          // Case 2b: setState('context', { ... }) - nest payload inside context
          else {
            set(state => ({
              ...state,
              [context]: payload,
            }));
          }
          return;
        }

        // Case 3: setState({ ... }) - legacy behavior, merge into root
        set(state => ({ ...state, ...contextOrPayload }));
      },
    }),
    { name: 'globalStore', enabled: true }
  )
);

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export const useGlobalStore = store;
export const globalStore = store.getState;

// *******************************************************************************
// SELECTORS (For non-React contexts)
// *******************************************************************************
export const fromGlobalStore = selector => {
  if (typeof selector === 'function') {
    return selector(useGlobalStore.getState());
  }
  return useGlobalStore.getState();
};
